// SPDX-FileCopyrightText: 2026 Z-Meet contributors
// SPDX-License-Identifier: Apache-2.0

// Nền video: làm mờ hoặc thay bằng ảnh.
//
// Chạy HOÀN TOÀN trên máy người dùng. Khung hình không đi qua server nào để xử
// lý, kể cả server của Z-Meet — nếu có thì đó là một dịch vụ xử lý video của
// bên thứ ba nhìn thấy phòng khách của mọi người.
//
// Asset của MediaPipe được bundle, không nạp từ CDN (xem scripts/vendor-mediapipe.mjs).

import { BackgroundProcessor } from '@livekit/track-processors'
import type { LocalVideoTrack } from 'livekit-client'

export type BackgroundMode = { kind: 'none' } | { kind: 'blur' } | { kind: 'image'; url: string }

// Đường dẫn tới asset đã bundle. Trùng với nơi vendor-mediapipe.mjs đặt chúng.
//
// ⚠️ Phải đi qua tuỳ chọn `assetPaths` của BackgroundProcessor, KHÔNG phải
// `segmenterOptions`. Hai hàm cũ BackgroundBlur/VirtualBackground nhận
// segmenterOptions ở tham số thứ hai, nên truyền nhầm chỗ khiến
// tasksVisionFileSet không bao giờ tới FilesetResolver — thư viện lặng lẽ quay
// về CDN jsdelivr, đúng thứ vendor-mediapipe.mjs tồn tại để tránh. Lỗi đó
// không nhìn thấy được ở máy có mạng: nó chỉ hiện ra ở môi trường air-gapped.
//
// Model bản `landscape` (144×256) chứ không phải bản vuông 256×256. Camera hội
// họp là khung 16:9; đưa nó vào một model vuông là nén ngang ~1,78× trước khi
// suy luận, và cạnh dọc — hai bên đầu, hai vai — mất chính xác nhiều nhất đúng
// ở chỗ người dùng nhìn vào. Hai model cùng 2 lớp và cùng ~250 KB.
const assetPaths = {
  tasksVisionFileSet: '/mediapipe/wasm',
  modelAssetPath: '/mediapipe/selfie_segmenter_landscape.tflite',
}

/** blurRadius vừa đủ để che nội dung phía sau mà không biến người thành viền răng cưa. */
const blurRadius = 12

/**
 * applyBackground gắn hoặc gỡ bộ xử lý nền cho track camera của chính mình.
 *
 * Ném lỗi khi asset thiếu hoặc thiết bị không kham nổi. Người gọi PHẢI bắt và
 * nói cho người dùng biết: im lặng thất bại ở đây nghĩa là họ tưởng nền đã bật
 * trong khi phòng của họ vẫn hiện ra với tất cả mọi người.
 */
export async function applyBackground(track: LocalVideoTrack, mode: BackgroundMode) {
  if (mode.kind === 'none') {
    await track.stopProcessor()
    return
  }
  const processor =
    mode.kind === 'blur'
      ? BackgroundProcessor({ mode: 'background-blur', blurRadius, assetPaths })
      : BackgroundProcessor({ mode: 'virtual-background', imagePath: mode.url, assetPaths })
  await track.setProcessor(processor)
}

/**
 * supported cho biết trình duyệt có chạy được bộ xử lý nền không.
 *
 * Kiểm TRƯỚC khi hiện nút thay vì để người dùng bấm rồi nhận lỗi: Safari cũ và
 * phần lớn trình duyệt di động thiếu MediaStreamTrackProcessor, và không có
 * cách nào lách được — đó là giới hạn của nền tảng, không phải của mã này.
 */
export function supported(): boolean {
  return 'MediaStreamTrackProcessor' in globalThis
}

// ---------------------------------------------------------------------------
// Ảnh nền do người dùng tự tải lên
// ---------------------------------------------------------------------------

// ⚠️ Ảnh KHÔNG được gửi lên server, và đó là điểm mấu chốt chứ không phải một
// đường tắt để đỡ việc.
//
// Toàn bộ tính năng nền tồn tại để phòng khách của người dùng không hiện ra
// trong cuộc họp. Gửi chính bức ảnh nền lên máy chủ Z-Meet đổi một vấn đề
// riêng tư lấy một vấn đề riêng tư khác — và kéo theo kho lưu trữ, hạn mức,
// quét nội dung, cùng nghĩa vụ xoá khi người dùng đóng tài khoản.
//
// Hệ quả phải nói thẳng: ảnh nền KHÔNG theo người dùng sang máy khác. Muốn vậy
// thì nó phải nằm ở server, và đó là một quyết định về sản phẩm chứ không phải
// một thiếu sót kỹ thuật.

const STORE_KEY = 'zmeet_backgrounds'

/** Số ảnh giữ lại. Đủ cho "nhà" và "văn phòng" mà không nuốt hết localStorage. */
export const MAX_IMAGES = 4

/**
 * Kích thước lưu. Khớp với độ phân giải video, không phải với ảnh gốc.
 *
 * Một tấm ảnh điện thoại 4000px là 3 MB trong localStorage — vượt hạn mức ~5 MB
 * chỉ sau hai ảnh — và bộ phân đoạn cũng chỉ dùng tới 720p. Thu nhỏ trước khi
 * lưu giải quyết cả hai.
 */
const STORE_W = 1280
const STORE_H = 720

export type StoredBackground = { id: string; dataURL: string }

export function listImages(): StoredBackground[] {
  try {
    const raw = localStorage.getItem(STORE_KEY)
    const list = raw ? (JSON.parse(raw) as StoredBackground[]) : []
    return Array.isArray(list) ? list.filter((b) => typeof b?.dataURL === 'string') : []
  } catch {
    // localStorage hỏng hoặc bị chặn (chế độ riêng tư của một số trình duyệt).
    // Mất ảnh đã lưu là phiền, không phải lỗi — tính năng vẫn dùng được.
    return []
  }
}

function write(list: StoredBackground[]) {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(list))
  } catch {
    // Hết hạn mức. Người gọi đã thấy ảnh áp dụng thành công; im lặng ở đây
    // đúng hơn là ném một lỗi giữa lúc họ đang trong cuộc họp.
  }
}

export function removeImage(id: string): StoredBackground[] {
  const list = listImages().filter((b) => b.id !== id)
  write(list)
  return list
}

/**
 * addImage thu nhỏ, mã hoá và lưu một ảnh người dùng chọn.
 *
 * Ném lỗi khi file không phải ảnh hoặc trình duyệt không giải mã được — người
 * gọi phải nói cho người dùng biết. Im lặng thất bại nghĩa là họ bấm rồi không
 * thấy gì xảy ra và không biết vì sao.
 */
export async function addImage(file: File): Promise<StoredBackground[]> {
  if (!file.type.startsWith('image/')) throw new Error('not an image')

  const bitmap = await createImageBitmap(file)
  // Thu về vừa khung, giữ nguyên tỉ lệ rồi cắt phần thừa: ảnh bị bóp méo làm
  // nền trông sai ngay từ cái nhìn đầu tiên.
  const scale = Math.max(STORE_W / bitmap.width, STORE_H / bitmap.height)
  const w = Math.round(bitmap.width * scale)
  const h = Math.round(bitmap.height * scale)

  const canvas = document.createElement('canvas')
  canvas.width = STORE_W
  canvas.height = STORE_H
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('canvas unavailable')
  ctx.drawImage(bitmap, (STORE_W - w) / 2, (STORE_H - h) / 2, w, h)
  bitmap.close()

  const dataURL = canvas.toDataURL('image/webp', 0.8)
  // Ảnh mới lên đầu, cắt bớt cái cũ nhất. Không hỏi người dùng xoá cái nào:
  // họ vừa chọn một ảnh, và một hộp thoại ngay lúc đó là chắn đường.
  const list = [{ id: String(Date.now()), dataURL }, ...listImages()].slice(0, MAX_IMAGES)
  write(list)
  return list
}
